<template>
    <form class="col-md-8 mx-auto" ref="form">
        <app-popup ref="appPopup"></app-popup>
        <app-alert ref="appAlert"></app-alert>

        <div class="btn-group" role="group" aria-label="Basic example">
            <button type="button"
                    class="btn btn-primary"
                    @click="redirectToList">
                <i class="fas fa-th-list"></i> {{ $t('backToList') }}
            </button>
        </div>

        <div class="form-row">
            <h1>{{ $t('organizers') }} <span v-if="!isNew && organizer.deactivated"
                                             class="badge badge-pill badge-danger">{{ $t('buttons.deactivate.status') }}</span>
            </h1><!-- TODO: -->
        </div>

        <div class="loader" v-if="saving"></div>

        <div v-if="!saving">
            <div class="card">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('company.cardTitle') }}</div>
                </div>
                <div class="card-body">
                    <company
                        :company="organizer.company"
                        :max-length="4000"
                        :vat-number-required="false"
                        :removable-manager="true"
                        :removable-contact="true"
                    />
                </div>
            </div>

            <br/>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('places.otherFields') }}</div>
                </div>

                <div class="card-body">
                    <other-fields :otherFields="organizer.otherFields"></other-fields>
                </div>
            </div>

            <br/>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('contract.cardTitle') }}</div>
                </div>

                <div class="card-body">
                    <contract v-for="(contract,index) in organizer.contracts"
                            :key="index"
                              @deleted="contractDeleted"
                              @uidChanged="contract.uid = $event"
                              :contract.sync="contract"
                              :title="$t('contract.cardTitle')"></contract>
                </div>

                <div class="card-header">
                    <button type="button" class="btn btn-primary"
                            @click="createContract">
                        <i class="far fa-plus-square"></i>
                        {{ $t('buttons.create.btnText') }}
                    </button>
                </div>
            </div>

            <br/>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('documents') }}</div>
                </div>
                <div class="card-body">
                    <div v-for="(document,index) in organizer.documents" :key="index" class="mt-2 border border-light-2 p-2">
                        <div class="form-control">
                            <label :for="'document' + _uid" class="font-weight-bold">{{$t('document')}}</label>
                            <i class="fas fa-trash-alt float-right btn-danger pointer"
                               @click="deleteDocument(document,index)"></i>
                        </div>
                        <div class="mt-2">
                            <file-download :file="document"></file-download>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <div v-for="(file,index) in organizer.files" :key="index" class="mt-2 border border-light-2 p-2">
                        <div class="form-control">
                            <label :for="'document' + _uid" class="font-weight-bold">{{$t('document')}} </label>
                            <i class="fas fa-trash-alt float-right btn-danger pointer"
                               @click="deleteFile(index)"></i>
                        </div>
                        <div class="mt-2">
                            <file-upload :file="file"></file-upload>
                        </div>
                    </div>
                </div>
                <div class="card-header">
                    <button type="button" class="btn btn-primary" @click="addFile">
                        <i class="far fa-plus-square"></i>
                        {{ $t('publicOrder.addFile') }}
                    </button>
                </div>
            </div>

            <AuxiliaryRegister v-if="isLoaded"
                               ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.Organizers_Partners"
                               register-name="Organizers"
                               :entry-id="organizer.auxRegisterEntityId"></AuxiliaryRegister>
        </div>

        <div class="text-center mt-3">
            <button type="button" class="btn btn-primary btn" @click="onSubmit" :disabled="saving">
                <i class="fas fa-save"></i>
                {{ $t('buttons.save.btnText') }}
            </button>
        </div>
    </form>
</template>

<script>
    import CompanyTemplate from './../../../components/company/Company';
    import ContractTemplate from './../../../components/contract/Contract';
    import Alert from '../../../components/common/Alert.vue';
    import MsgPopup from '../../../components/common/MsgPopup.vue';
    import OtherFields from '../../../components/otherFields/OtherFields.vue';
    import FileDownload from '../../../components/common/FileDownload.vue';
    import FileUpload from '../../../components/common/FileUpload.vue';
    import store from '../../../store/store';
    import { AuxiliaryRegisters } from '@/models/enums';
    import { Organizer, Contract, OrganizerActivity } from '@/models/models';
    import { File } from '@/models/file.models';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        props: {
            id: Number
        },
        components: {
            "company": CompanyTemplate,
            'contract': ContractTemplate,
            'app-alert': Alert,
            'app-popup': MsgPopup,
            'other-fields': OtherFields,
            'file-upload': FileUpload,
            FileDownload,
            AuxiliaryRegister: AuxiliaryRegister
        },
        data() {
            return {
                organizer: new Organizer({}, true, true),
                isLoaded: false,
                saving: false,
                isSaved: false,
                AuxiliaryRegisters: AuxiliaryRegisters
            }
        },
        mounted: function () {
            if (!this.isNew) {
                this.load();
            } else {
                this.isLoaded = true;
                this.$store.initialModel = _.cloneDeep(this.organizer);
            }
        },
        beforeRouteLeave(to, from, next) {
            if (!_.isEqual(this.$store.initialModel, this.organizer) && !this.isSaved) {
                const answer = window.confirm(this.$t('notifications.leavePage'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },
        computed: {
            isNew: function () {
                return this.id === undefined;
            }
        },
        methods: {
            load() {
                this.$http.get('/api/organizers/' + this.id)
                    .then(response => {
                        this.organizer = new Organizer(response.data, true, true);
                        this.$store.initialModel = _.cloneDeep(this.organizer);
                        this.isLoaded = true;
                    }).catch(error => {
                    this.saving = false;

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                });
            },
            createContract: function () {
                this.organizer.contracts.push(new Contract());
            },
            contractDeleted: function (m) {
                this.organizer.contracts = this.organizer.contracts.filter(c => c.uid !== m.uid);
                this.$vs.notify({
                    title: this.$t('buttons.delete.tooltipText'),
                    text: this.$t('notifications.success'),
                    color: 'success',
                    time: store.getters.notificationDuration,
                })
            },
            createOrganizerActivities: function () {
                this.organizer.organizerActivities.unshift(new OrganizerActivity());
            },
            organizerActivityDeleted: function (m) {
                this.organizer.organizerActivities = this.organizer.organizerActivities.filter(c => c.uid !== m.uid);
                this.$vs.notify({
                    title: this.$t('buttons.delete.tooltipText'),
                    text: this.$t('notifications.success'),
                    color: 'success',
                    time: store.getters.notificationDuration,
                })
            },
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.saving = true;

                        this.organizer.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();

                        if (this.isNew) {
                            this.$http.post('/api/Organizers/', this.organizer
                            ).then((response) => {
                                this.updateIds(response.data.contractIds);
                                this.isSaved = true;
                                this.saving = false;
                                this.$vs.notify({
                                    title: response.request ? this.$t('request.response.status.' + response.request.status) : this.$t('request.response.status.200'),
                                    text: response.data.message ? response.data.message : (response.request.statusText ? response.request.statusText : response.message),
                                    color: 'success',
                                    time: store.getters.notificationDuration,
                                })
                                this.organizer.id = result;
                                this.uploadFiles(result.data);
                                this.redirectToList();
                            }).catch(error => {
                                this.saving = false;

                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: 'danger',
                                    time: this.$store.getters.notificationDuration
                                })
                            })
                        } else {
                            this.$http.put('/api/Organizers/', this.organizer
                            ).then((response) => {
                                this.updateIds(response.data.contractIds);
                                this.isSaved = true;
                                this.saving = false;
                                this.$vs.notify({
                                    title: response.request ? this.$t('request.response.status.' + response.request.status) : this.$t('request.response.status.200'),
                                    text: response.data.message ? response.data.message : (response.request.statusText ? response.request.statusText : response.message),
                                    color: 'success',
                                    time: store.getters.notificationDuration,
                                })
                                this.uploadFiles(this.id);
                                this.redirectToList();
                            }).catch(error => {
                                this.saving = false;

                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: 'danger',
                                    time: this.$store.getters.notificationDuration
                                })
                            })
                        }
                    }
                });
            },
            uploadFiles(registerId) {
                // TODO: Loading with upload files text for example
                this.loading = true;
                if (this.organizer.files.length > 0) {
                    let formData = new FormData();
                    let descriptions = [];
                    this.organizer.files.forEach(file => {
                        formData.append('files', file.content, file.fileName);
                        descriptions.push({
                            fileName: file.fileName,
                            description: file.fileDescription,
                            length: file.content.size
                        });
                    });

                    try {
                        const descJson = JSON.stringify(descriptions);
                        formData.append('descriptionJson', descJson);
                    } catch (e) {
                        console.error(e);
                    }

                    this.$http.post(`/api/Organizers/uploadfiles/${registerId}`, formData).then(result => {
                        this.redirectToList();
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successUploadingFiles'),
                            position: 'top-center',
                            color: 'success'
                        });
                    }).catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$t('notifications.errorUploadingFiles'),
                            position: 'top-center',
                            color: 'danger'
                        });
                    });
                } else {
                    this.redirectToList();
                }
            },
            updateIds(contractIds) {
                if (!contractIds) {
                    return;
                }

                for (let i = 0; i < contractIds.length; i++) {
                    let id = contractIds[i].id;
                    let index = this.organizer.contracts.findIndex((e) => e.uid === contractIds[i].uid)
                    if (index >= 0 && this.organizer.contracts[index].id !== id) {
                        this.organizer.contracts[index].id = id;
                    }
                }
            },
            redirectToList() {
                this.$router.push({ path: '/registers/organizers' });
            },
            addFile(event) {
                this.organizer.files.push(new File('', ''));
            },
            deleteFile: function (m) {
                this.organizer.files.splice(m, 1);
            },
            deleteDocument: function (doc, index) {
                this.organizer.deletedDocumentsIds.push(doc.id);
                this.organizer.documents.splice(index, 1);
            },
            fillDummyData: function () {
                this.organizer.company.name = 'Radko Inc.';
                this.organizer.company.vatNumber = 'BG175415627';
                this.organizer.company.bulstatUic = '175415627';
                this.organizer.company.phone = '00359777666444';
                this.organizer.company.fax = '0035925566221';
                this.organizer.company.url = 'www.testoooooov.bg';
                this.organizer.company.email = 'dummy@test.bg';
                this.organizer.company.description = 'Тестови данни';

                this.organizer.company.manager.firstName = 'Тесто';
                this.organizer.company.manager.lastName = 'Тестов';
                // this.organizer.company.manager.personIdentification.identificationTypeId = '4641';
                // this.organizer.company.manager.personIdentification.number = '8112077286';
                // this.organizer.company.manager.genderTypeId = '4644';
                this.organizer.company.manager.position = 'шефче';
                this.organizer.company.manager.email = 'dummy@test.bg';
                this.organizer.company.manager.phone = '0035925566221';
                this.organizer.company.manager.mobilePhone = '00359777666444';

                this.organizer.company.registeredAddress.postCode = '1582';
                this.organizer.company.registeredAddress.street = 'ул. Тестова';
                this.organizer.company.registeredAddress.num = '13';
                this.organizer.company.registeredAddress.settlementId = '2643';

                this.organizer.company.bankAccount.bankName = 'Общинска Банка АД';
                this.organizer.company.bankAccount.bic = 'UBBSBGSFXXX';
                this.organizer.company.bankAccount.iban = 'BG21SOMB91301026369201';
                this.organizer.company.bankAccount.description = 'Тестови данни';
            }
        }
    }
</script>

<style scoped>
</style>
